<template>
    <section>
        <div class="row">
            <div class="col-12 col-sm-6">
                <h4 class="text-uppercase mb-3">Funding sources</h4>
            </div>
            <div class="col-12 col-sm-6">
                <a
                    class="btn btn-sm font-weight-bold text-white text-uppercase float-right"
                    :class="canEditClientFunding ? 'btn-primary' : 'btn-light cursor-not-allowed'"
                    @click="canEditClientFunding ? openNewFundingSourceModal() : null"
                    >Add new funding source</a
                >
            </div>
        </div>

        <p v-if="!fundings.length" class="font-italic">
            There are no funding sources associated with this client.
        </p>

        <div
            v-else=""
            class="alert alert-info alert-dismissible fade show d-flex align-items-center mb-3"
            role="alert"
        >
            <i class="fa fa-info-circle text-secondary mr-2"></i>You can copy an existing funding
            from the the "View details" screen.
            <button type="button" class="close" data-dismiss="alert" aria-label="Close">
                <span aria-hidden="true">&times;</span>
            </button>
        </div>

        <div class="my-2" v-for="(f, fundingIndex) in fundings">
            <div
                class="table-header font-weight-bold text-white w-100 p-2 d-flex justify-content-between align-items-center"
            >
                <span>{{ f.source ? f.source.name : "" }}</span>
                <span class="ml-auto mr-2 text-white">View contracts:</span>
                <span class="mt-2">
                    <toggle-button
                        :value="viewActive[fundingIndex]"
                        color="#D64571"
                        :width="80"
                        :height="30"
                        @change="$set(viewActive, fundingIndex, $event.value)"
                        :sync="true"
                        :labels="{ checked: 'Active', unchecked: 'Archived' }"
                    />
                </span>
            </div>
            <div class="bg-white w-100 border-light p-3 mb-5">
                <template
                    v-for="(c, index) in f.contracts.filter(contract =>
                        viewActive[fundingIndex] ? !contract.is_archived : contract.is_archived
                    )"
                >
                    <hr v-if="index > 0" class="bar my-2" />
                    <div class="m-3 p-3">
                        <div class="row">
                            <div class="col-9 col-sm-10 source-details">
                                <p class="m-1">
                                    <span
                                        class="text-capitalize font-weight-bold"
                                        v-if="f.source && f.source.type === 'insurance_provider'"
                                        >Member ID#:
                                    </span>
                                    <span
                                        class="text-capitalize font-weight-bold"
                                        v-if="!f.source || f.source.type !== 'insurance_provider'"
                                        >Authorization number:
                                    </span>
                                    <span>{{ c.identifier }}</span>
                                </p>
                                <p
                                    class="m-1"
                                    v-if="f.source && f.source.type === 'insurance_provider'"
                                >
                                    <span class="text-capitalize font-weight-bold"
                                        >Authorization number:
                                    </span>
                                    <span>{{ c.authorization_number }}</span>
                                </p>
                                <p class="m-1 d-flex">
                                    <span class="text-capitalize font-weight-bold"
                                        >Authorization start date:
                                    </span>
                                    <span>{{
                                        c.start_at ? moment(c.start_at).format("MM/DD/YYYY") : ""
                                    }}</span>
                                </p>
                                <p class="m-1 d-flex">
                                    <span class="text-capitalize font-weight-bold"
                                        >Authorization end date:
                                    </span>
                                    <span>{{
                                        c.end_at ? moment(c.end_at).format("MM/DD/YYYY") : ""
                                    }}</span>
                                </p>
                                <p class="m-1">
                                    <span class="text-capitalize font-weight-bold"
                                        >Next report due:
                                    </span>
                                    <span>{{
                                        c.report_due_at
                                            ? moment(c.report_due_at).format("MM/DD/YYYY")
                                            : ""
                                    }}</span>
                                </p>
                                <p
                                    class="m-1"
                                    v-if="f.source && f.source.type === 'insurance_provider'"
                                >
                                    <span class="text-capitalize font-weight-bold"
                                        >Diagnoses:
                                    </span>
                                    <span v-for="d in c.diagnoses" class="comma-list">{{
                                        d.service_nickname
                                    }}</span>
                                </p>
                                <p
                                    class="m-1"
                                    v-if="f.source && f.source.type === 'insurance_provider'"
                                >
                                    <span class="text-capitalize font-weight-bold"
                                        >CPT Codes:
                                    </span>
                                    <span v-for="cc in c.cpt_codes" class="comma-list">{{
                                        cc.service_nickname
                                    }}</span>
                                </p>
                                <p
                                    class="m-1"
                                    v-if="f.source && f.source.type !== 'insurance_provider'"
                                >
                                    <span class="text-capitalize font-weight-bold"
                                        >Service Codes:
                                    </span>
                                    <span v-for="s in c.service_codes" class="comma-list">{{
                                        s.service_nickname
                                    }}</span>
                                </p>
                                <template v-if="c.options">
                                    <p
                                        v-for="k in Object.keys(c.options)"
                                        v-if="![].includes(k)"
                                        class="m-1"
                                    >
                                        <span class="text-capitalize"
                                            >{{ k.replace(/_/g, " ") }}:
                                        </span>
                                        <span>{{ c.options[k] }}</span>
                                    </p>
                                </template>
                            </div>
                            <div class="col-2 text-center" v-if="f.source">
                                <h1 class="text-success">
                                    {{
                                        calculateUsage(
                                            c.approved_used,
                                            c.approved_max_total_calculated
                                        )
                                    }}%
                                </h1>
                                <div class="text-success">Used</div>
                                <template v-if="c.approved_used && c.approved_max_total_calculated">
                                    <div class="text-success small">
                                        {{ c.approved_unit_type === "amount" ? "$" : ""
                                        }}{{ c.approved_used }}
                                        out of
                                        {{ c.approved_unit_type === "amount" ? "$" : ""
                                        }}{{ c.approved_max_total_calculated }}
                                        {{ c.approved_unit_type === "session" ? "sessions" : "" }}
                                        {{ c.approved_unit_type === "minute" ? "minutes" : "" }}
                                    </div>
                                </template>
                            </div>
                        </div>
                        <div class="row mt-3">
                            <div class="col-12 col-sm-6 pl-2">
                                <button
                                    v-if="!c.is_archived"
                                    class="btn btn-link btn-sm"
                                    @click="archiveContract(f, c)"
                                >
                                    Move funding source to archive list
                                </button>
                            </div>
                            <div
                                class="col-12 col-sm-6 d-flex justify-content-end align-items-center"
                            >
                                <button
                                    class="btn btn-sm btn-primary ml-3"
                                    @click="editContract(f, c)"
                                >
                                    View details<i class="fa fa-angle-right ml-2"></i>
                                </button>
                            </div>
                        </div>
                    </div>
                </template>
                <p
                    class="p-3"
                    v-if="
                        viewActive[fundingIndex] &&
                            !f.contracts.filter(contract => !contract.is_archived).length
                    "
                >
                    No active contracts.
                </p>
                <p
                    class="p-3"
                    v-if="
                        !viewActive[fundingIndex] &&
                            !f.contracts.filter(contract => contract.is_archived).length
                    "
                >
                    No archived contracts.
                </p>
            </div>
        </div>

        <modal-component
            :id="`new-funding-source`"
            :visible="modal.visible"
            :title="modal.title"
            :size="modal.size"
            @close="resetModal()"
        >
            <funding-new
                ref="newModal"
                :patient="patient"
                :funding-sources="fundingSources"
                :cpt-codes="cptCodes"
                :service-codes="serviceCodes"
                :diagnoses="diagnoses"
                :appointment-types="appointmentTypes"
                @copy-funding="modal.title = `New patient funding source`"
                @new-entry="refreshFundings()"
                @close="resetModal()"
            ></funding-new>
        </modal-component>
    </section>
</template>

<script>
import FundingNew from "./PatientFundingNew";
import ModalComponent from "../ui/ModalComponent";
import { permissions } from "../../plugins/permissions";
import { helpers } from "../../plugins/helpers";

export default {
    name: "PatientFunding",
    props: ["patient", "organization", "cptCodes", "serviceCodes", "diagnoses", "fundings"],
    mixins: [permissions],
    components: { FundingNew, ModalComponent },
    data() {
        return {
            fundingSources: [],
            appointmentTypes: [],
            modal: {
                visible: false,
                size: "lg",
                title: "New Patient Funding Source",
            },
            viewActive: [true],
        };
    },
    mounted() {
        this.initialize();
    },
    watch: {
        organization() {
            this.initialize();
        },
        fundings() {
            if (this.fundings.length) {
                this.viewActive = Array.from(this.fundings, () => true);
            }
        },
    },
    methods: {
        initialize() {
            if (this.organization && !this.appointmentTypes.length && !this.fundingSources.length) {
                this.fetchOrganizationSources();
                this.fetchAppointmentTypes();
            }
        },

        fetchOrganizationSources() {
            axios
                .get(`/api/organizations/${this.organization.id}/funding/sources`)
                .then(r => {
                    this.fundingSources = r.data.data;
                })
                .catch(e => {
                    console.error(e);
                });
        },

        fetchAppointmentTypes() {
            axios
                .get(`/api/organizations/${this.organization.id}/scheduling/appointment_types`)
                .then(r => {
                    this.appointmentTypes = r.data.data;
                })
                .catch(error => {
                    console.error(error);
                });
        },

        openNewFundingSourceModal() {
            this.modal.visible = true;
        },

        archiveContract(funding, contract) {
            if (!this.canEditClientFunding) return;
            let payload = { ...contract };
            payload.is_archived = 1;
            Object.keys(payload).forEach(k => {
                if (k.slice(-2) === "at") {
                    //converts dates to database format
                    if (payload[k]) {
                        payload[k] = this.moment(payload[k]).format("YYYY-MM-DD HH:mm:ss");
                    } else {
                        delete payload[k];
                    }
                }
            });
            helpers.cleanEmptyProperties(payload);
            payload = helpers.prepareCodesPayload(payload);
            axios
                .patch(
                    `/api/patients/${this.patient.id}/fundings/${funding.id}/contracts/${
                        payload.id
                    }`,
                    payload
                )
                .then(r => {
                    this.refreshFundings();
                })
                .catch(e => {
                    console.error(e);
                    let errorObject = _.get(e, "response.data.errors", {});
                    let errorDetails = "";
                    Object.keys(errorObject).forEach(
                        errorKey => (errorDetails += `${errorObject[errorKey]}<br>`)
                    );
                    this.$swal({
                        title: "Error",
                        html: `An error occurred while archiving this source. <br>${errorDetails}`,
                        type: "error",
                        confirmButtonText: "Close",
                        confirmButtonColor: "#d64571",
                    });
                });
        },

        refreshFundings() {
            this.$emit("refresh-fundings");
        },

        editContract(funding, contract) {
            this.modal.title = "Patient funding source details";
            this.$refs.newModal.cleanup();
            this.$refs.newModal.setEdit(funding, contract);
            this.openNewFundingSourceModal();
        },

        resetModal() {
            this.modal.title = "New patient funding source";
            this.modal.visible = false;
            this.editSource = null;
            this.$refs.newModal.cleanup();
        },

        calculateUsage(used, total) {
            used = parseFloat(used);
            total = parseFloat(total);

            if (total) {
                let usage = Math.round((100 * used) / total);
                if (usage) {
                    return usage;
                }
            }

            return 0;
        },
    },
};
</script>

<style scoped></style>
